import {
  Paper,
  Table,
  TableBody,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
  withStyles,
} from "@material-ui/core";
import { useTranslations } from "adapters/i18n";
import React, { useState } from "react";
import { isEven } from "shared/utils";
import {
  COLORS,
  getDetailedTableColumns,
  PAGE_TYPE,
  TABLE_COLUMNS,
  extractDetailedAppValue,
  extractDetailedPortalValue,
  TABLE_TYPE,
} from "../conciliacaoUtil";
import ConciliationDetailedTableRowCell from "./conciliationDetailedTableRowCell";
import ConciliationTableHeaderCell from "./conciliationTableHeaderCell";

export const ConciliationDetailedTable = ({
  classes,
  conciliationData,
  onGetDetailedCollectData,
  onShowDetailsModal,
  pageType,
}) => {
  const { t } = useTranslations();

  const [pagination, setPagination] = useState({
    page: 0,
    pageSize: 10,
  });

  const tableColumns = getDetailedTableColumns(t);
  const animalCategoryColumn = tableColumns.find(
    (col) => col.id === TABLE_COLUMNS.CATEGORY
  );

  const firstRowIndex = pagination.page * pagination.pageSize;
  const lastRowIndex = firstRowIndex + pagination.pageSize;
  const tableData =
    pageType === PAGE_TYPE.DEFAULT
      ? conciliationData.slice(firstRowIndex, lastRowIndex)
      : conciliationData;

  const handleChangePage = (_, page) => {
    setPagination((prev) => ({ ...prev, page }));
  };

  const handleChangePageSize = (event) => {
    setPagination((prev) => ({ ...prev, pageSize: event.target.value }));
  };

  const handleOpenDetailsModal = async (rowData) => {
    const animalCategoryData = rowData[animalCategoryColumn.field];

    await onGetDetailedCollectData(animalCategoryData.IdCategoriaAnimal);
    onShowDetailsModal(animalCategoryData.Nome);
  };

  const showFooter = pageType !== PAGE_TYPE.PRINT;

  return (
    <>
      <Typography
        id="detailed-table-title"
        className={classes.title}
        gutterBottom
      >
        {t("common:detailed")}
      </Typography>
      <Paper className={classes.containerTable}>
        <Table id="detailed-table" className={classes.table}>
          <colgroup>
            {tableColumns.map((col) => (
              <col
                key={`details-col-${col.id}-width`}
                style={{ width: "9%" }}
              />
            ))}
          </colgroup>

          <TableHead id="detailed-table-header">
            <TableRow style={{ backgroundColor: COLORS.GRAY }}>
              {tableColumns.map((column) => (
                <ConciliationTableHeaderCell
                  columnId={column.id}
                  key={`details-header-${column.id}`}
                  label={column.label}
                  pageType={pageType}
                  tableType={TABLE_TYPE.DETAILED}
                />
              ))}
            </TableRow>
          </TableHead>
          <TableBody id="detailed-table-body">
            {tableData.map((data, index) => (
              <TableRow
                key={`detailed-table-row-${index}`}
                id={`detailed-table-row-${index}`}
                className={classes.contentTableRow}
                style={{
                  backgroundColor: isEven(index + 1)
                    ? COLORS.VERY_LIGHT_GRAY
                    : undefined,
                }}
              >
                {tableColumns.map((column) => (
                  <TableRowCell
                    column={column}
                    data={data}
                    handleOpenDetailsModal={handleOpenDetailsModal}
                    pageType={pageType}
                    key={`detailed-column-cell-${column.id}`}
                  />
                ))}
              </TableRow>
            ))}
          </TableBody>
          {showFooter && (
            <TableFooter id="detailed-table-footer">
              <TableRow>
                <TablePagination
                  count={conciliationData.length || 0}
                  page={pagination.page}
                  rowsPerPage={pagination.pageSize}
                  rowsPerPageOptions={[10, 20, 50]}
                  labelRowsPerPage={t("common:pagination.rows-per-page")}
                  onChangePage={handleChangePage}
                  onChangeRowsPerPage={handleChangePageSize}
                />
              </TableRow>
            </TableFooter>
          )}
        </Table>
      </Paper>
    </>
  );
};

const TableRowCell = ({ column, data, handleOpenDetailsModal, pageType }) => {
  const { t } = useTranslations();

  const appValue = extractDetailedAppValue({
    columnId: column.id,
    columnField: column.field,
    data,
    t,
  });

  const portalValue = extractDetailedPortalValue({
    columnId: column.id,
    columnField: column.field,
    data,
    t,
  });

  const handleOpenModal = () => {
    handleOpenDetailsModal(data);
  };

  return (
    <ConciliationDetailedTableRowCell
      appValue={appValue}
      columnId={column.id}
      onOpenDetailsModal={handleOpenModal}
      pageType={pageType}
      portalValue={portalValue}
    />
  );
};

export const styles = () => ({
  title: {
    fontSize: "16px",
    fontWeight: "500",
    fontFamily: "Roboto",
    color: "#424242",
    marginBottom: "20px",
    textTransform: "uppercase",
  },
  containerTable: {
    borderRadius: "16px",
  },
  table: {
    width: "100%",
    marginTop: "25px",
    marginBottom: "25px",
    fontFamily: "Roboto",
    fontStretch: "normal",
    fontStyle: "normal",
    lineHeight: "normal",
    letterSpacing: "1px",
    borderRadius: "0 0 16px 16px",
  },
  contentTableRow: {
    height: "66px",
    maxHeight: 66,
    fontFamily: "Roboto",
  },
});

export default withStyles(styles)(ConciliationDetailedTable);
